import { Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import BaselinePage from './pages/BaselinePage';
import SystemsPage from './pages/SystemsPage';
import DecisionPage from './pages/DecisionPage';
import RecommendationPage from './pages/RecommendationPage';
import ExportPage from './pages/ExportPage';
import StoryUIPage from './pages/StoryUIPage';
import StoryEntryPage from './pages/StoryEntryPage';
import StoryViewPage from './pages/StoryViewPage';
import CustomerStoryPage from './pages/CustomerStoryPage';
import CustomerPortalPage from './pages/CustomerPortalPage';
import WelcomePage from './pages/WelcomePage';
import AboutPage from './pages/AboutPage';
import DecisionBriefPage from './pages/DecisionBriefPage';
import TurboPage from './pages/TurboPage';
import LegalPage from './pages/LegalPage';
import RadialSwotDemoPage from './pages/RadialSwotDemoPage';
import DiscoveryHubDemo from './pages/DiscoveryHubDemo';
import PortalPage from './pages/PortalPage';
import GlassBoxPage from './pages/dev/GlassBoxPage';
import { HubPage } from './components/hub/HubPage';
import { RecommendationProvider } from './context/RecommendationContext';
import { ExperienceProvider } from './context/ExperienceContext';
import { Footer } from './components/layout/Footer';
import { DevOverlay, DevSidebar, shouldShowDevtools } from './devtools';
import ErrorBoundary from './components/ErrorBoundary';

/**
 * Atlas PHM — Application Router
 *
 * Atlas routing model: one interactive UI + downstream views.
 *
 * ┌─────────────────────────────────────────────────────────────────────┐
 * │  SITE-VISIT MODE (single interactive UI)                           │
 * │  └─ Main UI         → /hub (default route: /)                      │
 * │      Explainer-first, non-linear, context-driven                   │
 * │                                                                    │
 * │  PORTAL (Decision Map — primary portal UI)                         │
 * │  └─ Decision Map    → /portal/view/:docId                         │
 * │      Non-linear, explorable, A vs A+ vs B                         │
 * │                                                                    │
 * │  STORY (legacy) — kept for backwards compatibility                 │
 * │  └─ Story UI        → /story/ui/:docId                            │
 * │      Linear narrative with curated animations & docs               │
 * │                                                                    │
 * │  HUB (two-page assessment UI)                                      │
 * │  └─ Hub             → /hub                                         │
 * │      Page 1: System choice with intake tiles                       │
 * │      Page 2: Optimisation workbench with scope builder             │
 * └─────────────────────────────────────────────────────────────────────┘
 */
function App() {
  const location = useLocation();
  const isExportPage = location.pathname === '/app/export' || location.pathname === '/story/brief' || location.pathname === '/story/presentationPrint';
  const isPortalPage = location.pathname.startsWith('/portal/view');
  const isStoryUIPage = location.pathname === '/story/interactive' || location.pathname.startsWith('/story/ui') || location.pathname === '/' || isPortalPage;
  const isStoryEntryPage = location.pathname === '/story' || location.pathname.startsWith('/story/') && !location.pathname.startsWith('/story/interactive') && !location.pathname.startsWith('/story/ui') && !location.pathname.startsWith('/story/brief') && !location.pathname.startsWith('/story/presentationPrint');
  const isWelcomePage = location.pathname === '/welcome';
  const isAboutPage = location.pathname === '/about';
  const isTurboPage = location.pathname === '/turbo';
  const isLegalPage = location.pathname === '/legal';
  const isHubPage = location.pathname === '/hub' || location.pathname.startsWith('/hub/');
  const isClassicUI = location.pathname.startsWith('/app');
  const showDevtools = shouldShowDevtools();
  const showDevNavigation = showDevtools || (typeof window !== 'undefined' && (localStorage.getItem('atlas_dev') === '1' || !import.meta.env.PROD));

  return (
    <div className="app">
      {!isExportPage && !isStoryUIPage && !isWelcomePage && !isAboutPage && isClassicUI && (
        <nav className="app-nav">
          <div className="nav-container">
            <h1 className="nav-title">System Recommendation Tool</h1>
            <div className="nav-links">
              <Link 
                to="/app" 
                className={location.pathname === '/app' ? 'nav-link active' : 'nav-link'}
              >
                Baseline
              </Link>
              <Link 
                to="/app/systems" 
                className={location.pathname === '/app/systems' ? 'nav-link active' : 'nav-link'}
              >
                Systems
              </Link>
              <Link
                to="/app/decision"
                className={location.pathname === '/app/decision' ? 'nav-link active' : 'nav-link'}
              >
                Decision
              </Link>
              <Link
                to="/app/recommendation"
                className={location.pathname === '/app/recommendation' ? 'nav-link active' : 'nav-link'}
              >
                Recommendation
              </Link>
              <Link 
                to="/app/export" 
                className="nav-link export-link"
              >
                Export/Print
              </Link>
              <Link 
                to="/" 
                className="nav-link back-link"
              >
                ← Home
              </Link>
              <Link
                to="/story/interactive"
                className="nav-link story-link"
              >
                📖 Story UI
              </Link>
              {showDevNavigation && (
                <Link
                  to="/dev/glass-box"
                  className={location.pathname === '/dev/glass-box' ? 'nav-link active' : 'nav-link'}
                >
                  Glass Box
                </Link>
              )}
            </div>
          </div>
        </nav>
      )}

      <main className={`app-main ${isStoryUIPage || isStoryEntryPage || isWelcomePage || isAboutPage || isTurboPage || isLegalPage || isHubPage ? 'no-padding' : ''}`}>
        <ExperienceProvider>
          <Routes>
            {/* ─── Home (defaults to Site-Visit Mode) ─── */}
            <Route path="/" element={<HubPage />} />

            {/* ─── Legacy shell routes (repointed to Site-Visit Mode) ─── */}
            <Route path="/story" element={<StoryEntryPage />} />
            <Route path="/story/interactive" element={<StoryUIPage />} />
            <Route path="/story/interactive/:docId" element={<StoryUIPage />} />
            <Route path="/expert/journey" element={<StoryUIPage />} />
            {/* Story (legacy) — linear narrative */}
            <Route path="/story/ui" element={<CustomerStoryPage />} />
            <Route path="/story/ui/:docId" element={<CustomerStoryPage />} />
            <Route path="/story/:code" element={<StoryViewPage />} />
            
            {/* ─── Portal Animations Demo ─── */}
            <Route path="/portal" element={<CustomerPortalPage />} />

            {/* ─── DECISION MAP PORTAL ─── */}
            {/* New non-linear decision map UI (per PORTAL_UI_SPEC.md) */}
            <Route path="/portal/view" element={
              <ErrorBoundary context="Decision Map Portal">
                <RecommendationProvider>
                  <PortalPage />
                </RecommendationProvider>
              </ErrorBoundary>
            } />
            <Route path="/portal/view/:docId" element={
              <ErrorBoundary context="Decision Map Portal">
                <RecommendationProvider>
                  <PortalPage />
                </RecommendationProvider>
              </ErrorBoundary>
            } />

            {/* ─── STORY (LEGACY): Linear narrative results ─── */}
            {/* The legacy story UI is the existing /story/ui/:docId route (CustomerStoryPage). */}
            {/* Kept for backwards compatibility. "Portal" now means /portal/view/:docId.       */}

            {/* ─── HUB: Two-page system assessment UI (main UI) ─── */}
            <Route path="/hub" element={<HubPage />} />
            <Route path="/hub/:docId" element={<HubPage />} />

            {/* ─── Classic UI pages ─── */}
            <Route path="/welcome" element={<WelcomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/legal" element={<LegalPage />} />
            <Route path="/turbo" element={<TurboPage />} />
            <Route path="/demo/radial-swot" element={<RadialSwotDemoPage />} />
            <Route path="/demo/discovery-hub" element={<DiscoveryHubDemo />} />
            <Route path="/dev/glass-box" element={showDevNavigation ? <GlassBoxPage /> : <Navigate to="/" replace />} />
            <Route path="/app" element={<BaselinePage />} />
            <Route path="/app/systems" element={<SystemsPage />} />
            <Route path="/app/decision" element={<DecisionPage />} />
            <Route path="/app/recommendation" element={<RecommendationPage />} />
            <Route path="/app/export" element={
              <ErrorBoundary context="Export Page">
                <RecommendationProvider>
                  <ExportPage />
                </RecommendationProvider>
              </ErrorBoundary>
            } />
            <Route path="/story/brief" element={
              <ErrorBoundary context="Decision Brief">
                <RecommendationProvider>
                  <DecisionBriefPage />
                </RecommendationProvider>
              </ErrorBoundary>
            } />
            <Route path="/story/presentationPrint" element={
              <ErrorBoundary context="Presentation Print">
                <RecommendationProvider>
                  <ExportPage />
                </RecommendationProvider>
              </ErrorBoundary>
            } />

            {/* ─── Catch-all: unknown routes ─── */}
            <Route path="*" element={
              <div style={{ padding: '2rem', textAlign: 'center' }}>
                <h2>Route not found</h2>
                <p>The page <code>{location.pathname}</code> does not exist.</p>
              </div>
            } />
          </Routes>
        </ExperienceProvider>
      </main>

      {!isExportPage && !isStoryUIPage && !isStoryEntryPage && (
        <Footer />
      )}

      {showDevtools && (
        <DevOverlay />
      )}
      {showDevtools && (
        <DevSidebar />
      )}
    </div>
  );
}

export default App;
